/**
 * @file sales-trend.tsx
 * @module app/modules/sales-management/sales-manager/dashboard/chart/sales-trend
 * @description Implements the sales trend interface for sales-management operations.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 1.2.5
 */

import React, { useState, useEffect } from "react";
import { Col } from "react-bootstrap";
import Chart from "react-apexcharts";
import { getCSSVariableValue } from "../../../../../../_admin/assets/ts/_utils";
import Method from "../../../../../../utils/methods";
import APICallService from "../../../../../../api/apiCallService";
import { Dashboard } from "../../../../../../utils/constants";
import { dashboard, salesDashboard } from "../../../../../../api/apiEndPoints";
import Loader from "../../../../../../Global/loader";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const SalesTrend = () => {
  const [chartData, setChartData] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [ordersData, setOrderData] = useState<any>();
  const [allDates, setAllDates] = useState<any[]>([]);
  const [startDate, setStartDate] = useState<any>(new Date());
  const baseColor = getCSSVariableValue("--bs-primary");

  useEffect(() => {
    const firstDay = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    const lastDay = new Date(
      startDate.getFullYear(),
      startDate.getMonth() + 1,
      0
    );
    fetchReports(firstDay, lastDay, undefined);
  }, [startDate]);

  const fetchReports = async (fromDate: any, toDate: any, district: any) => {
    setLoading(true);
    let params: any = {
      fromDate: Method.convertDateToFormat(fromDate, "YYYY-MM-DD"),
      toDate: Method.convertDateToFormat(toDate, "YYYY-MM-DD"),
      // utcOffset: new Date().getTimezoneOffset(),
      // reportType: "0",
    };
    if (district !== undefined) {
      params = { ...params, "districts[]": district._id };
    }
    // if (selectedWarehouse?.value) {
    //   params = {
    //     ...params,
    //     warehouse: selectedWarehouse?.value,
    //   };
    // }
    let apiService = new APICallService(
      salesDashboard.weeklySalesTrendsReport,
      params,
      "",
      "",
      "",
      "",
      Dashboard
    );
    let response = await apiService.callAPI();
    if (response && response.length) {
      // Normalize weekly response and fill missing weeks via util
      const normalized: any[] = Method.populateMissingWeeks(
        response,
        fromDate,
        toDate
      );
      const categories = normalized.map((n) => `Week ${n.week}`);
      setAllDates(categories);
      setChartData(normalized);
      setOrderData(normalized);
    } else {
      setChartData([]);
      setAllDates([]);
      setOrderData(null);
    }
    setLoading(false);
  };
  return (
    <>
      <Col sm={12} md={6} lg={6}>
        <div
          className="bg-f9f9f9 px-5 py-5"
          style={{ border: "0.5px solid #E0E0DF", borderRadius: "10px", height: "455px" }}
        >
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h3 className="mb-0 fw-bolder fs-18">Delivered orders sales trend</h3>
            <div className="w-150px">
              <DatePicker
                className="form-control form-control-solid bg-white border-gray-300 cursor-pointer"
                selected={startDate}
                onChange={(date: Date) => setStartDate(date)}
                dateFormat="MMM yyyy"
                showMonthYearPicker
                calendarClassName="sales-trend-month-picker"
                maxDate={new Date()}
              />
            </div>
          </div>
          {loading ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ height: "275px" }}
            >
              <Loader loading={loading} />
            </div>
          ) : (
            <Chart
              options={{
                chart: {
                  fontFamily: "inherit",
                  type: "area" as "area",
                  height: 275,
                  toolbar: {
                    show: false,
                  },
                  dropShadow: {
                    enabled: false,
                  },
                },
                dataLabels: {
                  enabled: false,
                },
                stroke: {
                  curve: "smooth",
                  show: true,
                  width: 3,
                  colors: [baseColor],
                },
                legend: {
                  show: false,
                },
                xaxis: {
                  type: "category",
                  categories: allDates,
                  axisBorder: {
                    show: false,
                  },
                  axisTicks: {
                    show: false,
                  },
                  labels: {
                    style: {
                      colors: "#1a1a1a",
                      fontSize: "12px",
                      fontFamily: "inherit",
                    },
                    formatter: function (value) {
                      if (typeof value === "string" && value.startsWith("Week")) {
                        return value;
                      }
                      return value;
                    },
                  },
                  tooltip: {
                    enabled: false,
                  },
                },
                yaxis: [
                  {
                    seriesName: "",
                    labels: {
                      align: "center",
                      style: {
                        colors: "#7c7c7c",
                        fontSize: "12px",
                        fontFamily: "inherit",
                      },
                      formatter: function (value) {
                        if (value === 0) return `Tsh 0`;
                        const formatted = Method.formatNumberWithSuffix(value);
                        const decimals = formatted.updatedNumber >= 10 ? 0 : 1;
                        return `Tsh ${Number(formatted.updatedNumber).toFixed(decimals)}${formatted.suffixStr}`;
                      },
                    },
                  },
                ],
                tooltip: {
                  enabled: true,
                  custom: function ({ series, seriesIndex, dataPointIndex, w }) {
                    const monthData = chartData[dataPointIndex] || {};
                    const val = monthData.totalOrderValue || 0;
                    let displayOrderValue = `Tsh 0`;
                    if (val !== 0) {
                      const formatted = Method.formatNumberWithSuffix(val);
                      const decimals = formatted.updatedNumber >= 10 ? 0 : 1;
                      displayOrderValue = `Tsh ${Number(formatted.updatedNumber).toFixed(decimals)}${formatted.suffixStr}`;
                    }
                    return `<div style='width:auto; min-width: 164px !important' class="bg-white p-3 rounded shadow-sm border">
                      <div class="mb-2">
                        <span class="d-block text-muted fs-12 mb-1">Total Orders</span>
                        <span class="d-block fw-bold fs-16 text-dark">${monthData.totalOrders || 0}</span>
                      </div>
                      <div class='separator border-gray-200 my-2'></div>
                      <div class="mb-0">
                        <span class="d-block text-muted fs-12 mb-1">Total order value</span>
                        <span class="d-block fw-bold fs-16 text-dark">${displayOrderValue}</span>
                      </div>
                    </div>`;
                  },
                },
                colors: [baseColor],
                grid: {
                  borderColor: "#e0e0df",
                  strokeDashArray: 5,
                  yaxis: {
                    lines: {
                      show: true,
                    },
                  },
                },
                fill: {
                  type: "solid",
                  opacity: 0.05,
                },
                markers: {
                  size: 0,
                  colors: baseColor,
                  strokeColors: "#fff",
                  strokeWidth: 2,
                  strokeOpacity: 1,
                  strokeDashArray: 0,
                  fillOpacity: 1,
                  discrete: [],
                  shape: "circle",
                  radius: 2,
                  offsetX: 0,
                  offsetY: 0,
                  onClick: undefined,
                  onDblClick: undefined,
                  showNullDataPoints: true,
                  hover: {
                    size: 5,
                    sizeOffset: 3,
                  },
                },
              }}
              series={[
                {
                  name: "",
                  data: chartData.map((entry: any) => Number(entry.totalOrderValue || 0)),
                },
              ]}
              type="area"
              height="350"
            />
          )}
        </div>
      </Col>
    </>
  );
};

export default SalesTrend;
